'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { AppHeader } from './AppHeader';
import { AppDownloadHeader } from '../app-download/AppDownloadHeader';
import { Footer } from './Footer';
import type { FooterData } from './Footer';
import { AuthenticatedFooter } from './AuthenticatedFooter';
import { cn } from '@/lib/utils';

import { NavCategory, ServiceList, DocumentList } from '@/types';

interface City {
  id: string;
  cityName: string;
  urlStructure: string;
}

interface MobileLayoutProps {
  children: React.ReactNode;
  showBottomNav?: boolean;
  showFooter?: boolean;
  cities?: City[];
  allCityNavigation?: Record<string, NavCategory[]>;
  initialCity?: string;
  allCityServices?: Record<string, ServiceList[]>;
  documents?: DocumentList[];
  allCityFooterData?: Record<string, FooterData | null>;
}

const CITY_STORAGE_KEY = 'docupro_selected_city';

const toUrlSlug = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const setCookie = (name: string, value: string) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; path=/; max-age=31536000; SameSite=Lax`;
};

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }
  return null;
};

export function MobileLayoutInner({
  children,
  showBottomNav = true,
  showFooter = true,
  cities = [],
  allCityNavigation = {},
  initialCity = 'bangalore',
  allCityServices = {},
  documents = [],
  allCityFooterData = {},
}: MobileLayoutProps) {
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [isChangingCity, setIsChangingCity] = useState(false);

  // Derive navigation and services for the currently selected city
  const currentCitySlug = selectedCity?.urlStructure || initialCity;
  const currentNavigation = allCityNavigation[currentCitySlug] || allCityNavigation[initialCity] || [];
  const currentServices = allCityServices[currentCitySlug] || allCityServices[initialCity] || [];

  const pathname = usePathname();

  // Initialize city from URL, cookie, or default (bangalore for SEO)
  useEffect(() => {
    const urlSegments = pathname.split('/').filter(Boolean);
    const firstSegment = urlSegments.length > 0 ? urlSegments[0] : null;

    // Check if first segment is a city
    const matchedCityFromUrl = cities.find(
      (c) =>
        (c.urlStructure && c.urlStructure === firstSegment) ||
        toUrlSlug(c.cityName) === firstSegment
    );

    // Try saved city from localStorage/cookie
    let savedCity: City | null = null;
    try {
      const saved = localStorage.getItem(CITY_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        savedCity = cities.find((c) => c.id === parsed.id) || null;
      }
    } catch {
      // Ignore localStorage errors
    }

    // Priority: URL city > Saved city > Default (bangalore)
    let cityToUse: City | null = null;

    if (matchedCityFromUrl) {
      cityToUse = matchedCityFromUrl;
      // Sync cookie with URL city
      const citySlug = matchedCityFromUrl.urlStructure || toUrlSlug(matchedCityFromUrl.cityName);
      setCookie('selectedCity', citySlug);
    } else if (savedCity) {
      cityToUse = savedCity;
    } else {
      // Default to bangalore (or initialCity) for SEO
      cityToUse = cities.find(
        (c) => c.urlStructure === initialCity || toUrlSlug(c.cityName) === initialCity
      ) || cities[0] || null;
    }

    if (cityToUse && (!selectedCity || selectedCity.id !== cityToUse.id)) {
      setSelectedCity(cityToUse);
    }
  }, [pathname, cities, initialCity]);

  // Handle city change with navigation
  const handleCityChange = async (city: City) => {
    if (isChangingCity) return;
    setIsChangingCity(true);

    const newCitySlug = city.urlStructure || toUrlSlug(city.cityName);

    // Save to localStorage and cookie
    try {
      localStorage.setItem(CITY_STORAGE_KEY, JSON.stringify(city));
      setCookie('selectedCity', newCitySlug);

      // Dispatch custom event for components that need to react to city changes
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('cityChanged'));
      }
    } catch (error) {
      console.error('Failed to save city selection:', error);
    }

    setSelectedCity(city);

    // Parse current path
    const urlSegments = pathname.split('/').filter(Boolean);
    const firstSegment = urlSegments.length > 0 ? urlSegments[0] : null;

    // Check if first segment is a city
    const isCurrentFirstSegmentCity = cities.some(
      (c) =>
        (c.urlStructure && c.urlStructure === firstSegment) ||
        toUrlSlug(c.cityName) === firstSegment
    );

    let candidatePath = `/${newCitySlug}`;

    // If we're on a city-specific page, try to maintain the path structure
    if (isCurrentFirstSegmentCity && firstSegment && urlSegments.length > 1) {
      const remainingPath = urlSegments.slice(1).join('/');
      candidatePath = `/${newCitySlug}/${remainingPath}`;
    }

    // Preserve UTM parameters and ref
    const mergedParams = new URLSearchParams(window.location.search);
    const keysToPersist = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'ref',
    ];

    keysToPersist.forEach((key) => {
      if (!mergedParams.has(key)) {
        const cookieVal = getCookie(key);
        if (cookieVal) mergedParams.set(key, cookieVal);
      }
    });

    const queryString = mergedParams.toString();
    const querySuffix = queryString ? `?${queryString}` : '';

    let finalTargetUrl = `${candidatePath}${querySuffix}`;
    const fallbackUrl = `/${newCitySlug}${querySuffix}`;

    // Check if the path exists for the new city, otherwise fall back to city home
    if (candidatePath !== `/${newCitySlug}`) {
      try {
        const response = await fetch(candidatePath, { method: 'HEAD' });
        if (response.status === 404) {
          finalTargetUrl = fallbackUrl;
        }
      } catch (error) {
        console.warn('Error checking path existence, falling back to city home', error);
        finalTargetUrl = fallbackUrl;
      }
    }

    // Navigate to the new URL
    window.location.assign(finalTargetUrl);
  };

  // /app is a standalone download landing page, not a site page — it gets
  // the logo-only header instead of the full nav/search/login chrome.
  const isAppDownloadPage = pathname === '/app';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      {isAppDownloadPage ? (
        <AppDownloadHeader />
      ) : (
        <AppHeader
          cities={cities}
          navigation={currentNavigation}
          selectedCity={selectedCity}
          onCityChange={handleCityChange}
          services={currentServices}
          documents={documents}
        />
      )}

      {/* Main Content - Add bottom padding when bottom nav is shown.
          max() fallback ensures Android gesture-nav phones (which often
          report 0 for env(safe-area-inset-bottom)) still clear the system bar. */}
      {/* Main Content.
          padding-top = full header height (content height + safe-area top)
          padding-bottom = full bottom-nav height (content + safe-area bottom)
          Both bars are position:fixed, so main needs to reserve their
          total height including the safe-area extension; otherwise page
          content gets hidden under the bar background. */}
      <main
        className={cn(
          'flex-1',
          // AppDownloadHeader sits in normal flow (it's taller than the
          // fixed --header-height slot every other page reserves), so this
          // page doesn't add the fixed-header offset on top of it. The
          // bottom nav is explicitly suppressed below for this page (see
          // the render below), so no padding is reserved for it either —
          // hero should run straight into the footer instead of leaving a
          // blank gap for a bar that isn't there.
          isAppDownloadPage
            ? undefined
            : 'pt-[calc(var(--header-height)+var(--safe-area-inset-top))] pb-[calc(var(--bottom-nav-height)+max(var(--safe-area-inset-bottom),12px))]'
        )}
      >
        {children}
      </main>

      {/* Footer - SEO-rich footer for non-authenticated users */}
      {showFooter && (
        <Footer
          initialCitySlug={selectedCity?.urlStructure || toUrlSlug(selectedCity?.cityName || 'bangalore')}
          allFooterData={allCityFooterData}
          cities={cities.map((c) => ({ name: c.cityName, slug: c.urlStructure }))}
        />
      )}

      {/* Bottom nav - shows for all users on all screen sizes, except the
          /app download page: it's a standalone landing page, and inside
          the native app (where this bar actually renders — it's a no-op
          on web) a tab bar back to the rest of the site doesn't belong on
          a page whose entire point is "get the app". */}
      {showBottomNav && !isAppDownloadPage && <AuthenticatedFooter />}
    </div>
  );
}

// Direct render - no Suspense wrapper needed since useSearchParams was removed.
// This ensures children (page content) are included in the initial SSR HTML for SEO.
export function MobileLayout(props: MobileLayoutProps) {
  return <MobileLayoutInner {...props} />;
}
